import React, { useState, useEffect } from "react";
import { getData } from "../../services/api.service";
import { generateOrderPDF } from "../../utils/pdfGenerator";
import { generateRemitoPDF } from "../../utils/remitoGenerator";

function OrderDetailModal({ isOpen, onClose, order }) {
  const [companySettings, setCompanySettings] = useState(null);
  
  useEffect(() => {
    if (isOpen) {
      loadCompanySettings();
    }
  }, [isOpen]);

  const loadCompanySettings = async () => {
    try {
      const response = await getData('/api/company-settings/remito', false);
      if (response?.data?.success) {
        setCompanySettings(response.data.data);
      }
    } catch (error) {
      console.error('Error cargando configuración de empresa:', error);
      // Si falla, el generador usará valores por defecto
    }
  };

  const handleGenerateRemito = async () => {
    await generateRemitoPDF(order, companySettings);
  };

  if (!isOpen || !order) return null;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-AR');
  };

  const calculateBusinessDays = (startDate, businessDays) => {
    if (!startDate || !businessDays) return null;
    
    const date = new Date(startDate);
    let daysAdded = 0;
    
    while (daysAdded < businessDays) {
      date.setDate(date.getDate() + 1);
      const dayOfWeek = date.getDay();
      // Si no es sábado (6) ni domingo (0), contar como día hábil
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        daysAdded++;
      }
    }
    
    return date;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      1: 'bg-warning text-dark', // Pedido
      2: 'bg-info', // En Taller
      3: 'bg-primary', // Listo
      4: 'bg-success', // Entregado
      5: 'bg-danger' // Cancelado
    };
    return statusMap[status] || 'bg-secondary';
  };

  return (
    <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title">
              DETALLE DEL PEDIDO (ID: #{order.order_id})
            </h5>
            <button 
              type="button" 
              className="btn-close btn-close-white" 
              onClick={onClose}
            ></button>
          </div>
          
          <div className="modal-body">
            {/* Información del Cliente */}
            <div className="row mb-3">
              <div className="col-md-6">
                <div className="border p-3 rounded">
                  <h6 className="text-primary mb-2">Nombre y apellido</h6>
                  <p className="mb-0">{order.client_name || 'Cliente directo'}</p>
                </div>
              </div>
              <div className="col-md-6">
                <div className="border p-3 rounded">
                  <h6 className="text-primary mb-2">DNI</h6>
                  <p className="mb-0">{order.client_dni || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Productos del Pedido - Información Detallada */}
            <div className="row mb-3">
              <div className="col-md-12">
                <div className="border p-3 rounded">
                  <h6 className="text-primary mb-3">Productos del Pedido</h6>
                  {order.items && order.items.length > 0 ? (
                    <div className="row">
                      {order.items.map((item, index) => {
                        const hasSku = item.sku_id && item.sku_id !== null;
                        const hasStockOnly = !hasSku && item.stock_products && item.stock_products.length > 0;
                        
                        return (
                        <div key={index} className="col-md-12 mb-3">
                          <div className="card">
                            <div className="card-body">
                              {/* Si es SOLO stock local (sin SKU) */}
                              {hasStockOnly ? (
                                <div className="alert alert-info mb-0">
                                  <h6 className="alert-heading mb-2">
                                    <i className="fas fa-boxes me-2"></i>
                                    Productos de Stock Local (sin fabricación)
                                  </h6>
                                  <ul className="mb-0">
                                    {item.stock_products.map((sp, spIndex) => (
                                      <li key={spIndex}>
                                        <strong>{sp.product_name}</strong> - 
                                        Cantidad: {sp.quantity} x {formatCurrency(sp.unit_price)} = 
                                        <strong className="text-success ms-1">
                                          {formatCurrency(sp.unit_price * sp.quantity)}
                                        </strong>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              ) : (
                                <>
                                  {/* Item normal con SKU */}
                                  <div className="row">
                                    <div className="col-md-6">
                                      <h6 className="card-title text-dark">
                                        {item.sku_description || `Producto ${index + 1}`}
                                      </h6>
                                      <p className="card-text mb-1">
                                        <strong>SKU:</strong> {item.sku || 'N/A'}
                                      </p>
                                      <p className="card-text mb-1">
                                        <strong>Cantidad:</strong> {item.quantity}
                                      </p>
                                      <p className="card-text mb-1">
                                        <strong>Precio unitario:</strong> {formatCurrency(item.sale_price)}
                                      </p>
                                      <p className="card-text">
                                        <strong>Subtotal:</strong> {formatCurrency(item.sale_price * item.quantity)}
                                      </p>
                                    </div>
                                    <div className="col-md-6">
                                      {item.details && (
                                        <>
                                          <p className="card-text mb-1">
                                            <strong>Material:</strong> {item.details.material_name || 'N/A'}
                                          </p>
                                          <p className="card-text mb-1">
                                            <strong>Medidas:</strong> {item.details.measurements || 'N/A'}
                                          </p>
                                          {item.details.main_accessory_name && (
                                            <p className="card-text mb-1">
                                              <strong>Accesorio principal:</strong> {item.details.main_accessory_name}
                                            </p>
                                          )}
                                          {item.details.additional_accessories && item.details.additional_accessories.length > 0 && (
                                            <p className="card-text mb-1">
                                              <strong>Accesorios adicionales:</strong>{' '}
                                              {item.details.additional_accessories.map(acc => acc.accessory_name).join(', ')}
                                            </p>
                                          )}
                                          {item.details.colors && item.details.colors.length > 0 && (
                                            <p className="card-text mb-1">
                                              <strong>Colores:</strong>{' '}
                                              {item.details.colors.map(color => color.color_name).join(', ')}
                                            </p>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {/* Productos de Stock Local del ítem (adicionales al SKU) */}
                                  {item.stock_products && item.stock_products.length > 0 && (
                                    <div className="row mt-3">
                                      <div className="col-12">
                                        <div className="alert alert-success mb-0">
                                          <h6 className="alert-heading mb-2">
                                            <i className="fas fa-boxes me-2"></i>
                                            Productos de Stock Local Incluidos:
                                          </h6>
                                          <ul className="mb-0">
                                            {item.stock_products.map((sp, spIndex) => (
                                              <li key={spIndex}>
                                                <strong>{sp.product_name}</strong> - 
                                                Cantidad: {sp.quantity} x {formatCurrency(sp.unit_price)} = 
                                                <strong className="text-success ms-1">
                                                  {formatCurrency(sp.unit_price * sp.quantity)}
                                                </strong>
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      )})}
                    </div>
                  ) : (
                    <p className="text-muted">No hay productos en este pedido</p>
                  )}
                </div>
              </div>
            </div>

            {/* Estado e ID */}
            <div className="row mb-3">
              <div className="col-md-6">
                <div className="border p-3 rounded">
                  <h6 className="text-primary mb-2">Estado</h6>
                  <span className={`badge ${getStatusBadgeClass(order.status)}`}>
                    {order.status_description || 'Pendiente'}
                  </span>
                </div>
              </div>
              <div className="col-md-6">
                <div className="border p-3 rounded">
                  <h6 className="text-primary mb-2">ID</h6>
                  <p className="mb-0">{order.order_id}</p>
                </div>
              </div>
            </div>

            {/* Fechas */}
            <div className="row mb-3">
              <div className="col-md-6">
                <div className="border p-3 rounded">
                  <h6 className="text-primary mb-2">Fecha de compra</h6>
                  <p className="mb-0">{formatDate(order.inserted_date)}</p>
                </div>
              </div>
              <div className="col-md-6">
                <div className="border p-3 rounded">
                  <h6 className="text-primary mb-2">Fecha de entrega estimada</h6>
                  {order.estimated_delivery_days ? (
                    <>
                      <p className="mb-1">
                        <strong>{order.estimated_delivery_days} días hábiles</strong>
                      </p>
                      {(() => {
                        const estimatedDate = calculateBusinessDays(
                          order.inserted_date,
                          order.estimated_delivery_days
                        );
                        return estimatedDate ? (
                          <p className="mb-0 text-muted small">
                            Aproximadamente: {formatDate(estimatedDate)}
                          </p>
                        ) : null;
                      })()}
                    </>
                  ) : (
                    <p className="mb-0">A definir</p>
                  )}
                </div>
              </div>
            </div>

            {/* Contacto */}
            <div className="row mb-3">
              <div className="col-md-6">
                <div className="border p-3 rounded">
                  <h6 className="text-primary mb-2">Teléfono</h6>
                  <p className="mb-0">{order.client_cellphone || 'No disponible'}</p>
                </div>
              </div>
              <div className="col-md-6">
                <div className="border p-3 rounded">
                  <h6 className="text-primary mb-2">Correo Electrónico</h6>
                  <p className="mb-0">{order.customer_email || 'No disponible'}</p>
                </div>
              </div>
            </div>

            {/* Precio y Dirección */}
            <div className="row mb-3">
              <div className="col-md-6">
                <div className="border p-3 rounded">
                  <h6 className="text-primary mb-2">Precio de venta</h6>
                  <h4 className="text-success mb-0">{formatCurrency(order.total)}</h4>
                  
                  {/* Desglose de precios: Producto Principal + Stock Local */}
                  {(() => {
                    let productTotal = 0;
                    let stockTotal = 0;
                    
                    if (order.items && order.items.length > 0) {
                      order.items.forEach(item => {
                        // Precio del producto principal
                        productTotal += (item.sale_price || 0) * (item.quantity || 0);
                        
                        // Precio de productos de stock local
                        if (item.stock_products && item.stock_products.length > 0) {
                          item.stock_products.forEach(sp => {
                            stockTotal += (sp.unit_price || 0) * (sp.quantity || 0);
                          });
                        }
                      });
                    }
                    
                    // Solo mostrar desglose si hay productos de stock local
                    if (stockTotal > 0) {
                      return (
                        <div className="mt-2 pt-2 border-top">
                          <small className="text-muted">
                            <strong>Desglose:</strong>
                          </small>
                          <br />
                          <small className="text-muted">
                            • Producto principal: {formatCurrency(productTotal)}
                          </small>
                          <br />
                          <small className="text-muted">
                            • Stock Local: {formatCurrency(stockTotal)}
                          </small>
                          <br />
                          <small className="text-muted">
                            <strong>Subtotal: {formatCurrency(productTotal + stockTotal)}</strong>
                          </small>
                        </div>
                      );
                    }
                  })()}
                  
                  {(order.interest_rate && parseFloat(order.interest_rate) > 0) && (
                    <div className="mt-2 pt-2 border-top">
                      <small className="text-muted">
                        + Interés ({parseFloat(order.interest_rate).toFixed(2)}%): {formatCurrency(order.interest_amount)}
                      </small>
                      <br />
                      <strong className="text-success">
                        Total a cobrar: {formatCurrency(order.total_with_interest)}
                      </strong>
                      <br />
                      <small className="text-muted">
                        {order.payment_installments} cuota{order.payment_installments > 1 ? 's' : ''} de {formatCurrency(order.installment_amount)}
                      </small>
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="border p-3 rounded">
                  <h6 className="text-primary mb-2">Dirección</h6>
                  <p className="mb-0">{order.delivery_address || order.client_address || 'No disponible'}</p>
                </div>
              </div>
            </div>

            {/* Pagos */}
            <div className="row mb-3">
              <div className="col-md-6">
                <div className="border p-3 rounded">
                  <h6 className="text-primary mb-2">Acreditado</h6>
                  <h5 className="text-success mb-0">{formatCurrency(order.credited_payment)}</h5>
                </div>
              </div>
              <div className="col-md-6">
                <div className="border p-3 rounded">
                  <h6 className="text-primary mb-2">Saldo</h6>
                  <h5 className="text-info mb-0">{formatCurrency(order.credit_balance)}</h5>
                </div>
              </div>
            </div>

            {/* Método de pago y transporte */}
            <div className="row mb-3">
              <div className="col-md-6">
                <div className="border p-3 rounded">
                  <h6 className="text-primary mb-2">Medio de pago</h6>
                  <p className="mb-0">{order.payment_description || 'No especificado'}</p>
                </div>
              </div>
              <div className="col-md-6">
                <div className="border p-3 rounded">
                  <h6 className="text-primary mb-2">Transporte</h6>
                  <p className="mb-0">{order.delivery_description || 'No especificado'}</p>
                </div>
              </div>
            </div>

            {/* Canal de Venta y Tipo de Cliente */}
            <div className="row mb-3">
              <div className="col-md-6">
                <div className="border p-3 rounded">
                  <h6 className="text-primary mb-2">Canal de Venta</h6>
                  <p className="mb-0">
                    {order.ecommerce_name || 'No especificado'}
                  </p>
                </div>
              </div>
              <div className="col-md-6">
                <div className="border p-3 rounded">
                  <h6 className="text-primary mb-2">Tipo de Cliente</h6>
                  <p className="mb-0">
                    {order.is_wholesale === 1 ? (
                      <span className="badge bg-primary">Mayorista</span>
                    ) : order.is_wholesale === 0 ? (
                      <span className="badge bg-secondary">Minorista</span>
                    ) : (
                      'No especificado'
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Comentarios y comisión */}
            <div className="row mb-3">
              <div className="col-md-6">
                <div className="border p-3 rounded">
                  <h6 className="text-primary mb-2">Comentarios</h6>
                  <p className="mb-0">{order.observations || 'Sin comentarios'}</p>
                </div>
              </div>
              <div className="col-md-6">
                <div className="border p-3 rounded">
                  <h6 className="text-primary mb-2">Comisión %</h6>
                  <p className="mb-0">
                    {order.commission_rate ? `${Number(order.commission_rate).toFixed(2)}%` : 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Historial de Actividades */}
            {order.activity_log && order.activity_log.length > 0 && (
              <div className="row mb-3">
                <div className="col-md-12">
                  <div className="border p-3 rounded">
                    <h6 className="text-primary mb-3">Historial del Pedido</h6>
                    <div className="timeline">
                      {order.activity_log.map((activity, index) => (
                        <div key={index} className="d-flex mb-2">
                          <div className="me-3">
                            <span className={`badge ${getStatusBadgeClass(activity.status)}`}>
                              {activity.status_description}
                            </span>
                          </div>
                          <div className="flex-grow-1">
                            <p className="mb-1">
                              <strong>{activity.activity_description}</strong>
                            </p>
                            <small className="text-muted">
                              {formatDateTime(activity.activity_date)} por {activity.activity_by_name}
                            </small>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button 
              type="button" 
              className="btn btn-success"
              onClick={handleGenerateRemito}
              title="Generar remito duplicado para transporte"
            >
              <i className="fas fa-file-invoice me-2"></i>
              Generar Remito
            </button>
            <button 
              type="button" 
              className="btn btn-primary"
              onClick={() => generateOrderPDF(order)}
              title="Descargar detalle en PDF"
            >
              <i className="fas fa-file-pdf me-2"></i>
              Imprimir Detalle
            </button>
            <button 
              type="button" 
              className="btn btn-outline-secondary"
              onClick={onClose}
            >
              <i className="fas fa-times me-2"></i>
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrderDetailModal;
